<template>
  <div class="fill-height column-format">
    <h-header></h-header>
    <div class="agreement-list portal-compact-list-shell">
      <v-data-table
          :items="filteredProposalList"
          :headers="headers"
          class="pointer portal-compact-table"
          :hide-default-footer="true"
          :disable-pagination="true"
          :mobile-breakpoint="200"
          fixed-header
          @click:row="openAgreement"
      >
        <template v-slot:item.name="{ item }">
          <span class="portal-cell-truncate portal-cell-primary" :title="item.name">{{ item.name }}</span>
        </template>
        <template v-slot:item.date="{ item }">
          <span class="portal-cell-truncate portal-cell-muted" :title="DateTime.fromISO(item.date).toLocaleString(DateTime.DATE_MED)">
            {{ DateTime.fromISO(item.date).toLocaleString(DateTime.DATE_MED) }}
          </span>
        </template>
        <template v-slot:item.dateCompleted="{ item }">
          <span
              v-if="item.dateCompleted"
              class="portal-cell-truncate portal-cell-muted"
              :title="DateTime.fromISO(item.dateCompleted).toLocaleString(DateTime.DATE_MED)"
          >
            {{ DateTime.fromISO(item.dateCompleted).toLocaleString(DateTime.DATE_MED) }}
          </span>
          <span v-else class="portal-cell-muted">—</span>
        </template>
        <template v-slot:item.status="{ item }">
          <portal-status-pill
              :color="`var(${statusDetail(item.status).color})`"
              :background="`var(${statusDetail(item.status).background})`"
          >
            {{ statusDetail(item.status).label }}
          </portal-status-pill>
        </template>
        <template v-slot:no-data>
          <div class="agreement-empty">
            <portal-icon name="handshake" :size="28"></portal-icon>
            <span>{{ $t('proposals.no-agreements') }}</span>
          </div>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import ProposalService from '@/modules/proposal/ProposalService';
import { DateTime } from 'luxon';
import ProposalDetails from '@/modules/proposal/ProposalDetails';
import HHeader from '@/components/HHeader.vue';
import AgreementService from '@/modules/agreements/AgreementService';

export default {
  name: 'ProposalList',

  components: {HHeader},

  mixins: [],

  data: function () {
    return {
      proposalService: new ProposalService(),
      agreementService: new AgreementService(),
      isReady: false,
      proposalList: [],
      agreementList: [],
      DateTime: DateTime,
    };
  },

  mounted() {
    this.getProposals();
    this.getAgreements();
  },

  beforeDestroy() {
  },

  methods: {
    getAgreements: function(){
      this.agreementService.getAgreementList().then((res) => {
        this.agreementList.splice(0, this.agreementList.length);
        this.agreementList.push(...res.data);
      })
    },

    getProposals: function () {
      this.proposalService.getProposalList().then((res) => {
        this.proposalList.splice(0, this.proposalList.length);
        this.proposalList.push(...res.data);
        this.isReady = true;
      });
    },

    openAgreement: function(agreement){
      if(agreement.version === 1){
        this.openProposal(agreement.id);
      }else{
        window.open(`/document/${agreement.id}${agreement.signer ? '.' + agreement.signer.id : ''}`,'_blank');
      }
    },

    openProposal: function (id) {
      let binding = {
        proposalId: id,
      }
      this.$store.state.globalModalController
          .openModal(ProposalDetails, binding, false, true)
          .then(() => this.getProposals())
    },

    sortByDate: function (a, b) {
      return b.date.localeCompare(a.date);
    },

    statusDetail(status) {
      let background, color;

      if (status === 'Signed' || status === 'Fully Executed') {
        background = '--v-success_10-base';
        color = '--v-success-base';
      } else if (status === 'Declined') {
        background = '--v-alert_10-base';
        color = '--v-alert-base';
      } else if (status === 'Sent') {
        background = '--v-warning_10-base';
        color = '--v-warning-base';
      } else {
        background = '--v-gray_20-base';
        color = '--v-gray_80-base';
      }

      if (status === 'Sent') {
        status = this.$t('proposals.status.ready-for-signature')
      } else if (status === 'Signed') {
        status = this.$t('proposals.status.signed')
      } else if (status === 'Fully Executed') {
        status = this.$t('proposals.status.fully-executed')
      } else if (status === 'declined') {
        status = this.$t('proposals.status.declined')
      }


      return { background, color, label: status };
    },
  },

  watch: {},

  computed: {
    headers: function() {
      return [
        {
          text: 'Name',
          value: 'name',
          class: 'portal-list-grow',
          cellClass: 'portal-list-grow',
        },
        {
          text: 'Date created',
          value: 'date',
          width: 146,
          class: 'portal-list-priority-low',
          cellClass: 'portal-list-priority-low',
        },
        {
          text: 'Date signed',
          value: 'dateCompleted',
          width: 146,
          class: 'portal-list-priority-medium',
          cellClass: 'portal-list-priority-medium',
        },
        { text: 'Status', value: 'status', width: 154 },
      ];
    },

    normalizedProposals: function() {
      let result = [];

      this.proposalList.forEach((p) => {
        result.push({
          id: p.id,
          name: p.coverPage?.heading,
          client: p.client,
          date: p.dateCreated,
          dateCompleted: p.dateSigned,
          status: p.proposalStatus,
          type: p.proposalType,
          lastEvent: p.lastEvent,
          version: 1,
        });
      });

      return result;
    },

    normalizedAgreements: function() {
      let result = [];

      this.agreementList.forEach((a) => {
        result.push({
          id: a.id,
          name: a.name,
          client: a.client,
          date: a.dateCreated,
          dateCompleted: a.dateCompleted,
          status: a.fullyExecuted ? 'Fully Executed' : a.status,
          type: 'Agreement',
          lastEvent: a.lastEvent,
          version: 2,
          signer: a.signers.find(s => s.email === this.$store.state.loggedInUser.email)
        });
      });

      return result;
    },

    filteredProposalList: function() {
      let result = [];
      result.push(...this.normalizedProposals);
      result.push(...this.normalizedAgreements);
      result.sort(this.sortByDate);
      return result;
    }
  },
};
</script>

<style lang="scss">
.agreement-list {
  min-height: 0;
  padding: 16px;
}

.agreement-empty {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--portal-muted);
}

.proposal-wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.proposal-card {
  width: 260px;
  height: 310px;
  border: 1px solid var(--v-gray_50-base);
  border-radius: 4px;
  box-shadow: 0px 1px 0px var(--v-gray_50-base);
  cursor: pointer;

  &:hover {
    background-color: var(--v-sidebar-base);
    transition: all 500ms ease;
  }
}

.image-preview {
  width: 259px;
  height: 140px;
  background-size: cover;
  border-radius: 4px 4px 0px 0px;
}


</style>
